/** Helpers for group payloads from GET /groups and GET /groups/manager/{id}. */

export function unwrapList(data) {
   if (Array.isArray(data)) return data;
   if (!data || typeof data !== "object") return [];

   const direct = [
      data.data,
      data.groups,
      data.items,
      data.users,
      data.content,
      data.result,
      data.list,
   ];
   for (const value of direct) {
      if (Array.isArray(value)) return value;
   }

   // { data: { users: [...] } } / { data: { content: [...] } }
   if (data.data && typeof data.data === "object" && !Array.isArray(data.data)) {
      return unwrapList(data.data);
   }
   return [];
}

export function unwrapEntity(data) {
   if (data == null || typeof data !== "object" || Array.isArray(data)) {
      return data;
   }
   if (data.data && typeof data.data === "object" && !Array.isArray(data.data)) {
      return data.data;
   }
   return flattenGroupAssignment(data);
}

/**
 * GET /groups/manager/{id} returns assignment rows:
 * { id: assignmentId, managerId, group: { id, name, professorId, ... } }
 * Flatten only those rows so we keep professorId + managerId on the group.
 */
export function flattenGroupAssignment(item) {
   if (item == null || typeof item !== "object" || Array.isArray(item)) {
      return item;
   }
   const nested = item.group;
   const isAssignment =
      nested &&
      typeof nested === "object" &&
      !Array.isArray(nested) &&
      nested.id != null &&
      Object.prototype.hasOwnProperty.call(item, "managerId");
   if (!isAssignment) return item;
   return {
      ...nested,
      managerId: item.managerId ?? nested.managerId ?? null,
      professorId: nested.professorId ?? item.professorId ?? null,
      professor: nested.professor ?? item.professor,
      assignmentId: item.id,
   };
}

function positiveId(raw) {
   if (raw && typeof raw === "object") {
      return positiveId(
         raw.userId ?? raw.user?.id ?? raw.id ?? raw.professorId,
      );
   }
   const n = Number(raw);
   return Number.isFinite(n) && n > 0 ? Math.trunc(n) : undefined;
}

function personDisplayName(person) {
   if (!person) return "";
   if (typeof person === "string") return person.trim();
   return (
      `${person.firstName || person.firstname || ""} ${
         person.lastName || person.lastname || ""
      }`.trim() ||
      String(person.name || person.fullName || person.email || "").trim()
   );
}

/** PATCH /groups/{id} uses professorId; GET may nest professor. */
export function readGroupProfessorId(group) {
   return positiveId(
      group?.professorId ??
         group?.profesorId ??
         group?.professor_id ??
         group?.professor?.userId ??
         group?.professor?.user?.id ??
         group?.professor ??
         group?.profesor,
   );
}

export function readGroupProfessorName(group, usersById) {
   const nested = group?.professor ?? group?.instructor ?? group?.profesor;
   const fromNested = personDisplayName(nested);
   if (fromNested) return fromNested;

   const fromField = String(
      group?.professorName ?? group?.instructorName ?? "",
   ).trim();
   if (fromField) return fromField;

   const id = readGroupProfessorId(group);
   if (id && usersById) {
      return personDisplayName(usersById.get(String(id))) || "";
   }
   return "";
}

/**
 * Manager assignment lives on POST/DELETE /groups/manager/...
 * GET /groups may still expose managerId / manager / managers[].
 */
export function readGroupManagerId(group) {
   const nested = Array.isArray(group?.managers)
      ? group.managers[0]
      : undefined;
   const fromIds = Array.isArray(group?.managerIds)
      ? group.managerIds[0]
      : undefined;
   return positiveId(
      group?.managerId ??
         group?.manager_id ??
         group?.assignedManagerId ??
         fromIds ??
         group?.manager ??
         nested,
   );
}

export function readGroupManagerName(group, usersById) {
   const nested = Array.isArray(group?.managers)
      ? group.managers[0]
      : group?.manager;
   const fromNested = personDisplayName(nested);
   if (fromNested) return fromNested;

   const fromField = String(group?.managerName ?? "").trim();
   if (fromField) return fromField;

   const id = readGroupManagerId(group);
   if (id && usersById) {
      return personDisplayName(usersById.get(String(id))) || "";
   }
   return "";
}

export function readGroupToken(group) {
   const raw = group?.token ?? group?.key ?? group?.groupToken ?? "";
   return String(raw).trim();
}

export function readGroupSector(group) {
   const raw = String(group?.sector ?? group?.sectorName ?? "").trim();
   if (!raw) return "";
   const lower = raw.toLowerCase();
   if (lower.includes("bot")) return "Botanica";
   if (lower.includes("cio")) return "Ciocana";
   if (lower.includes("bui")) return "Buiucani";
   return raw;
}

export function readStudentGroupId(student) {
   return positiveId(student?.groupId ?? student?.group?.id);
}
